import { useMemo } from "react";

import { sortItems } from "@/Components/v2/molecule/WaterfallChart/WaterfallChart";
import { MetricType } from "@/shared/types";

import { RelianceRankingResponse } from "../api/useRelianceRankingV2";
import { useBusinessTermMapping } from "../utils/businessTermMapping";

type UseSalesCompositionChartOptionsProps = {
  relianceRankingData: RelianceRankingResponse | undefined;
  metricType: MetricType;
};

export const useSalesCompositionChartOptions = ({
  relianceRankingData,
  metricType,
}: UseSalesCompositionChartOptionsProps) => {
  const { mapBusinessTermName } = useBusinessTermMapping();
  const seriesData = useMemo(() => {
    if (!relianceRankingData?.chartView) {
      return {
        salesCompositionData: [],
        total: 0,
      };
    }

    let total = 0;
    const totals = relianceRankingData.chartView.reduce(
      (acc: Record<string, number>, item: any) => {
        const data = metricType === MetricType.SALES_UNITS ? item.units : item.contribution;
        if (!data) return acc;

        Object.entries(data).forEach(([key, value]) => {
          if (key !== "total") {
            acc[key] = (acc[key] || 0) + Number(value);
            total += Number(value);
          }
        });
        return acc;
      },
      {}
    );

    const seriesData = Object.entries(totals)
      .map(([name, value]) => {
        return {
          name: mapBusinessTermName(name),
          value: Number(value),
        };
      })
      .filter((item) => item.value !== 0)
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

    return {
      salesCompositionData: sortItems(seriesData),
      total,
    };
  }, [relianceRankingData?.chartView, metricType]);

  return {
    salesCompositionData: seriesData.salesCompositionData,
    total: seriesData.total,
  };
};
